import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './Component_MakeDecisionProtocol_StepProceed.scss';
import { getDispositionAction } from '../../../../../../../_utils/disposition-action.js';
import { getOrderResourceType } from '../../../../../../../_utils/get-resource-type.js';
import { PathwayStateType_Admit, PathwayStateType_Discharge, PathwayStateType_Initial, PathwayStateType_Reassess, TransitionCondition } from '@app/pathway-shared';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_OrderGroup, ModuleFE_EMInterface, ModuleFE_RuntimeOrder } from '@app/advisor-frontend';
import { ModuleFE_AdvisorAnalytics } from '../../../../../../../_analytics/consts.js';
import { OrderPlacedSource } from '../../../../../../../_analytics/types.js';
import { Button, ComponentSync, LL_V_L, ModuleFE_MouseInteractivity, mouseInteractivity_ToolTip, openContent, stopPropagation } from '@nu-art/thunderstorm-frontend/index';
import { exists, filterInstances } from '@nu-art/ts-common';
import { Advisor_DialogConfirmation } from '../../../../../Advisor_DialogConfirmation/Advisor_DialogConfirmation.js';
import { RuntimeOrderStatus_Enum } from '@app/advisor-shared';
import { ModuleFE_ProvideInfo } from '../../../../../../../modules/provide-info/ModuleFE_ProvideInfo.js';
import { PopUp_OrderGroupsResolver } from '../../../../../../pop-ups/PopUp_OrderGroupsResolver/PopUp_OrderGroupsResolver.js';
const unacceptableOrderStatus = [
    RuntimeOrderStatus_Enum.ACCEPTED,
    RuntimeOrderStatus_Enum.PENDING,
    RuntimeOrderStatus_Enum.FOUND_IN_FHIR,
    RuntimeOrderStatus_Enum.PENDING_SIGNATURE,
];
export class Component_MakeDecisionProtocol_StepProceed extends ComponentSync {
    __onReadOnlyChanged = () => this.forceUpdate();
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.nextStep = nextProps.nextStep;
        state.potentialOrders = this.resolvePotentialOrders(state.nextStep);
        state.isReassessment = state.nextStep.targetState.type === PathwayStateType_Reassess;
        state.isQuickExit = state.isReassessment && state.nextStep.originState.type === PathwayStateType_Initial;
        return state;
    }
    //######################### Logic #########################
    showOrder(_order, nextStep) {
        const order = typeof _order === 'string' ? ModuleFE_Advisor_Action.cache.unique(_order) : _order;
        if (!order)
            return false;
        const applicability = nextStep.ordersMeetsExpressionCondition;
        if (applicability && applicability[order._id] === false)
            return false;
        const rtOrder = ModuleFE_RuntimeOrder.cache.find((rtOrder) => {
            if (rtOrder.orderId !== order._id)
                return false;
            return unacceptableOrderStatus.includes(rtOrder.orderStatus);
        });
        return !exists(rtOrder);
    }
    resolvePotentialOrders(nextStep) {
        const potentialOrders = { preConditions: [], additional: [] };
        if (nextStep.targetState.type === PathwayStateType_Admit)
            potentialOrders.dispo = { label: 'Admit', resourceType: 'admit' };
        else if (nextStep.targetState.type === PathwayStateType_Discharge)
            potentialOrders.dispo = { label: 'Discharge', resourceType: 'discharge' };
        nextStep.preConditionOrders.groups.forEach(group => {
            const passingOrders = group.items.filter(item => this.showOrder(item.id, nextStep));
            if (!passingOrders.length)
                return;
            switch (passingOrders.length) {
                case 1: {
                    const order = ModuleFE_Advisor_Action.cache.unique(passingOrders[0].id);
                    if (!order)
                        return;
                    return potentialOrders.preConditions.push({
                        label: order.label ?? order.name,
                        resourceType: getOrderResourceType(order),
                        type: 'order',
                        id: order._id,
                    });
                }
                default: {
                    const firstOrder = ModuleFE_Advisor_Action.cache.unique(passingOrders[0].id);
                    potentialOrders.preConditions.push({
                        label: `[${passingOrders.length}] ${group.label ?? group.name}`,
                        resourceType: getOrderResourceType(firstOrder),
                        type: 'group',
                        id: group._id,
                    });
                }
            }
        });
        nextStep.transitionOrders.groups.forEach(group => {
            const passingOrders = group.items.filter(item => this.showOrder(item.id, nextStep));
            if (!passingOrders.length)
                return;
            switch (passingOrders.length) {
                case 1: {
                    const order = ModuleFE_Advisor_Action.cache.unique(passingOrders[0].id);
                    if (!order)
                        return;
                    return potentialOrders.additional.push({
                        label: order.label ?? order.name,
                        resourceType: getOrderResourceType(order),
                        associatedIds: [order._id]
                    });
                }
                default: {
                    const firstOrder = ModuleFE_Advisor_Action.cache.unique(passingOrders[0].id);
                    potentialOrders.additional.push({
                        label: `[${passingOrders.length}] ${group.label ?? group.name}`,
                        resourceType: getOrderResourceType(firstOrder),
                        associatedIds: passingOrders.map(item => item.id)
                    });
                }
            }
        });
        nextStep.preConditionOrders.orders.forEach((order) => {
            if (!this.showOrder(order, nextStep))
                return;
            potentialOrders.preConditions.push({
                label: order.label ?? order.name,
                resourceType: getOrderResourceType(order),
                type: 'order',
                id: order._id,
            });
        });
        nextStep.transitionOrders.orders.forEach(order => {
            if (!this.showOrder(order, nextStep))
                return;
            potentialOrders.additional.push({
                label: order.label ?? order.name,
                resourceType: getOrderResourceType(order),
                associatedIds: [order._id]
            });
        });
        return potentialOrders;
    }
    getLabel = () => {
        //Return quick exit label
        if (this.state.isReassessment)
            return 'Exit';
        //Return disposition action
        if (this.state.potentialOrders.dispo)
            return this.state.potentialOrders.dispo.label;
        //Return label for first pre-condition
        if (this.state.potentialOrders.preConditions.length)
            return this.state.potentialOrders.preConditions[0].label;
        //Return default
        return 'Proceed';
    };
    getTooltipContent = () => {
        if (this.state.isReassessment)
            return _jsx("div", { children: "Diagnosis is less likely. Exit the protocol and reassess patient" });
        const potentialOrders = this.state.potentialOrders;
        if (potentialOrders.preConditions.length) {
            const nodes = [];
            nodes.push(_jsx("div", { children: "Send orders to Epic:" }, 'pre-condition-title'));
            nodes.push(_jsx(LL_V_L, { className: 'order-list', children: potentialOrders.preConditions.map(order => _jsx("div", { className: 'bold', children: order.label }, order.label)) }, 'pre-condition-list'));
            return nodes;
        }
        if (potentialOrders.dispo) {
            return _jsx("div", { children: `${potentialOrders.dispo.resourceType === 'admit'
                    ? 'Admit'
                    : 'Discharge'} patient` });
        }
        return _jsx("div", { children: "Proceed to the next step" });
    };
    onStepProceed = (e) => {
        stopPropagation(e);
        if (ModuleFE_EMInterface.isReadonly())
            return;
        //Actual button click logic
        const proceedLogic = () => {
            const groupIds = this.state.potentialOrders.preConditions.filter(preCon => preCon.type === 'group').map(preCon => preCon.id);
            if (!groupIds.length)
                return this.proceedStepImpl();
            //Resolve groups
            return PopUp_OrderGroupsResolver.show(e, {
                groupIds,
                onSubmit: (orderIdMapper) => this.proceedStepImpl(orderIdMapper)
            });
        };
        const step = this.state.nextStep;
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip);
        if (step.transitionCondition !== TransitionCondition.RES_FUNC_FALSE) { //Step is not against Quai recommendation
            return proceedLogic();
        }
        else { //Step against recommendation, show confirmation dialog
            Advisor_DialogConfirmation.show({
                confirmCallback: () => proceedLogic(),
                content: () => {
                    const label = step.transition.label;
                    return _jsxs(_Fragment, { children: ['Are you sure you want to proceed to ', _jsxs("span", { style: { fontWeight: '600', fontSize: '15px' }, children: [label, "?"] })] });
                },
            });
        }
    };
    proceedStepImpl = async (groupOrderSelectionMap = {}) => {
        const potentialOrders = this.state.potentialOrders;
        const protocolData = this.props.outerContext
            ?? ModuleFE_ProvideInfo.getCurrentProtocolContextData()?.protocolData;
        await ModuleFE_ProvideInfo.protocol.proceedWithStep(this.state.nextStep, this.props.outerContext);
        await this.acceptPreConditions(potentialOrders.preConditions, groupOrderSelectionMap, protocolData);
        this.props.onPostSubmit?.();
    };
    acceptPreConditions = async (preConditions, groupOrderSelectionMap, protocolData) => {
        const orderIds = preConditions.reduce((ids, preCon) => {
            if (preCon.type !== 'order')
                return ids;
            return [...ids, preCon.id];
        }, []);
        preConditions.forEach(preCon => {
            if (preCon.type !== 'group')
                return;
            const group = ModuleFE_Advisor_OrderGroup.cache.unique(preCon.id);
            if (!group)
                return;
            orderIds.push(groupOrderSelectionMap[group._id] ?? group.items[0].id);
        });
        const rtOrders = filterInstances(orderIds.map(orderId => {
            return ModuleFE_RuntimeOrder.cache.find(rtOrder => rtOrder.orderId === orderId && rtOrder.orderStatus === RuntimeOrderStatus_Enum.RECOMMENDED);
        }));
        const orderAmount = await ModuleFE_RuntimeOrder.orderAll(rtOrders.map(rtOrder => rtOrder._id), undefined);
        if (orderAmount > 0)
            rtOrders.forEach(rtOrder => {
                const dbOrder = ModuleFE_Advisor_Action.cache.unique(rtOrder.orderId);
                ModuleFE_AdvisorAnalytics.trigger.orders.order_placed({
                    orderId: rtOrder.orderId,
                    orderLabel: dbOrder?.label ?? dbOrder?.name ?? 'N/A',
                    source: OrderPlacedSource.navigationPrecondition,
                    protocolId: protocolData?.pathway._id,
                    protocolLabel: protocolData?.getLabel(),
                    stepId: this.state.nextStep.transition._id,
                });
            });
    };
    //######################### Render #########################
    render() {
        const disabled = ModuleFE_EMInterface.isReadonly();
        const dispo = this.state.potentialOrders.dispo;
        const presentation = dispo ? getDispositionAction(dispo.resourceType) : undefined;
        const Icon = presentation?.Icon;
        return _jsxs(Button, { variant: presentation?.variant ?? 'primary', className: 'make-decision-protocol__step-proceed', disabled: disabled, onClick: this.onStepProceed, ...openContent.tooltip.left('tooltip__step-orders', () => this.getTooltipContent(), { offset: 10 }), children: [Icon && _jsx(Icon.component, {}), _jsx("div", { className: 'make-decision-protocol__step-proceed__label', children: this.getLabel() })] });
    }
}
//# sourceMappingURL=Component_MakeDecisionProtocol_StepProceed.js.map